import {
  Bell,
  BellOff,
  CalendarClock,
  CalendarMinus,
  CalendarPlus,
  Check,
  ChevronRight,
  Download,
  ExternalLink,
  LogIn,
  MessageSquare,
  Plus,
  RefreshCw,
  Share2,
  Shield,
  Trash2,
  UserRound,
  Users,
  Vote,
  X
} from "lucide-react";
import { FormEvent, TouchEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  availability as demoAvailability,
  currentDemoUserId,
  messages as demoMessages,
  pollOptions as demoPollOptions,
  pollVotes as demoPollVotes,
  polls as demoPolls,
  profiles as demoProfiles,
  registrations as demoRegistrations,
  sessions as demoSessions
} from "./demoData";
import { isSupabaseConfigured, supabase, vapidPublicKey } from "./supabase";
import type {
  Availability,
  ChatMessage,
  EvaSession,
  Poll,
  PollOption,
  PollVote,
  Profile,
  Registration,
  Team,
  TeamMember
} from "./types";
import { formatSessionDate, isAvailabilityMatching, registeredCount, sessionStatus, uid, visibleTakenSeats, weekdays } from "./utils";

type Tab = "sessions" | "availability" | "chat" | "polls" | "admin";
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};
type MentionTargets = {
  mentionedPlayerIds: string[];
  mentionAllTeam: boolean;
};
type SwipeState = {
  x: number;
  y: number;
  time: number;
};

const tabs: Array<{ id: Tab; label: string; icon: typeof CalendarClock }> = [
  { id: "sessions", label: "Sessions", icon: CalendarClock },
  { id: "availability", label: "Dispos", icon: Users },
  { id: "chat", label: "Chat", icon: MessageSquare },
  { id: "polls", label: "Sondages", icon: Vote },
  { id: "admin", label: "Admin", icon: Shield }
];

const pageTitles: Record<Tab, string> = {
  sessions: "Sessions",
  availability: "Dispos",
  chat: "Chat",
  polls: "Sondages",
  admin: "Admin"
};

const appRedirectOrigin = (import.meta.env.VITE_PUBLIC_APP_URL as string | undefined)?.replace(/\/$/, "");
const notificationIcon = "/app-icon-192.png";
const installDismissedKey = "eva-planning-install-dismissed";
const installCompletedKey = "eva-planning-installed";
const activeTabStorageKey = "eva-planning-active-tab";
const teamMentionLabel = "VRM";
const emptyProfile: Profile = { id: "", display_name: "Joueur", role: "player" };
const demoTeam: Team = { id: "demo-team", name: "EVA Planning", slug: "eva-planning" };
const activeTeamStorageKey = "eva-planning-active-team";
const teamWelcomeStoragePrefix = "eva-planning-team-welcome";
const initialProfiles = isSupabaseConfigured ? [] : demoProfiles;
const initialSessions = isSupabaseConfigured ? [] : demoSessions;
const initialRegistrations = isSupabaseConfigured ? [] : demoRegistrations;
const initialAvailability = isSupabaseConfigured ? [] : demoAvailability;
const initialMessages = isSupabaseConfigured ? [] : demoMessages;
const initialPolls = isSupabaseConfigured ? [] : demoPolls;
const initialPollOptions = isSupabaseConfigured ? [] : demoPollOptions;
const initialPollVotes = isSupabaseConfigured ? [] : demoPollVotes;
const initialCurrentUser = isSupabaseConfigured ? emptyProfile : demoProfiles[0];

function getInitialTab(): Tab {
  const tab = new URLSearchParams(window.location.search).get("tab");
  if (tabs.some((entry) => entry.id === tab)) return tab as Tab;

  const storedTab = localStorage.getItem(activeTabStorageKey);
  return tabs.some((entry) => entry.id === storedTab) ? (storedTab as Tab) : "sessions";
}

function getNotificationPermission(): NotificationPermission {
  return "Notification" in window ? Notification.permission : "default";
}

function isPwaInstalled() {
  return window.matchMedia("(display-mode: standalone)").matches || Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
}

function isIosDevice() {
  const platform = window.navigator.platform;
  const userAgent = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent) || (platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
}

function shouldShowInstallPrompt() {
  return !isPwaInstalled() && localStorage.getItem(installCompletedKey) !== "true" && localStorage.getItem(installDismissedKey) !== "true";
}

export default function App() {
  const [activeTab, setActiveTab] = useState<Tab>(() => getInitialTab());
  const [profiles, setProfiles] = useState<Profile[]>(initialProfiles);
  const [sessions, setSessions] = useState<EvaSession[]>(initialSessions);
  const [registrations, setRegistrations] = useState<Registration[]>(initialRegistrations);
  const [availability, setAvailability] = useState<Availability[]>(initialAvailability);
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [polls, setPolls] = useState<Poll[]>(initialPolls);
  const [pollOptions, setPollOptions] = useState<PollOption[]>(initialPollOptions);
  const [pollVotes, setPollVotes] = useState<PollVote[]>(initialPollVotes);
  const [currentUser, setCurrentUser] = useState<Profile>(initialCurrentUser);
  const [teams, setTeams] = useState<Team[]>(isSupabaseConfigured ? [] : [demoTeam]);
  const [activeTeamId, setActiveTeamId] = useState(() => (isSupabaseConfigured ? localStorage.getItem(activeTeamStorageKey) || "" : demoTeam.id));
  const [authUserId, setAuthUserId] = useState<string | null>(isSupabaseConfigured ? null : currentDemoUserId);
  const [authLoading, setAuthLoading] = useState(isSupabaseConfigured);
  const [authAllowed, setAuthAllowed] = useState(!isSupabaseConfigured);
  const [authRestoring, setAuthRestoring] = useState(isSupabaseConfigured);
  const [syncingEva, setSyncingEva] = useState(false);
  const [pseudoDraft, setPseudoDraft] = useState(initialCurrentUser.display_name);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(() => getNotificationPermission());
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(() => shouldShowInstallPrompt());
  const [showIosInstallGuide, setShowIosInstallGuide] = useState(false);
  const [welcomeTeam, setWelcomeTeam] = useState<Team | null>(null);
  const currentUserRef = useRef(currentUser);
  const profilesRef = useRef(profiles);
  const activeTabRef = useRef(activeTab);
  const swipeRef = useRef<SwipeState | null>(null);
  const visibleTabs = useMemo(
    () => tabs.filter((tab) => currentUser.role === "admin" || tab.id !== "admin"),
    [currentUser.role]
  );
  const activeTeam = teams.find((team) => team.id === activeTeamId) || teams[0] || null;

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    profilesRef.current = profiles;
  }, [profiles]);

  useEffect(() => {
    if (currentUser.role !== "admin" && activeTab === "admin") {
      setActiveTab("sessions");
    }
  }, [activeTab, currentUser.role]);

  useEffect(() => {
    activeTabRef.current = activeTab;
    localStorage.setItem(activeTabStorageKey, activeTab);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", activeTab);
    window.history.replaceState({}, "", url);
  }, [activeTab]);

  useEffect(() => {
    if (activeTeamId) localStorage.setItem(activeTeamStorageKey, activeTeamId);
  }, [activeTeamId]);

  useEffect(() => {
    if (!isSupabaseConfigured || !authUserId || !activeTeam || !currentUser.id) return;

    const storageKey = `${teamWelcomeStoragePrefix}:${currentUser.id}:${activeTeam.id}`;
    if (localStorage.getItem(storageKey) === "true") return;

    localStorage.setItem(storageKey, "true");
    setWelcomeTeam(activeTeam);
  }, [activeTeam, authUserId, currentUser.id]);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
      if (shouldShowInstallPrompt()) setShowInstallPrompt(true);
    };
    const onAppInstalled = () => {
      localStorage.setItem(installCompletedKey, "true");
      setShowInstallPrompt(false);
      setInstallPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    if (isPwaInstalled()) {
      localStorage.setItem(installCompletedKey, "true");
      setShowInstallPrompt(false);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (!supabase) return;
    const client = supabase;

    async function restoreSession() {
      setAuthRestoring(true);
      try {
        const { data } = await withTimeout(client.auth.getSession(), 5000);
        const user = data.session?.user ?? null;
        setAuthUserId(user?.id ?? null);
        setAuthAllowed(true);
        if (user) {
          setCurrentUser((profile) => ({
            ...profile,
            id: user.id,
            display_name: profile.id === user.id ? profile.display_name : user.user_metadata?.name || user.email?.split("@")[0] || "Joueur",
            role: profile.id === user.id ? profile.role : "player"
          }));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setAuthLoading(false);
        setAuthRestoring(false);
      }
    }

    restoreSession();

    const { data } = client.auth.onAuthStateChange((_event, session) => {
      const user = session?.user ?? null;
      setAuthUserId(user?.id ?? null);
      setAuthAllowed(true);
      if (user) {
        setCurrentUser((profile) => ({
          ...profile,
          id: user.id,
          display_name: profile.id === user.id ? profile.display_name : user.user_metadata?.name || user.email?.split("@")[0] || "Joueur",
          role: profile.id === user.id ? profile.role : "player"
        }));
      }
      setAuthLoading(false);
      setAuthRestoring(false);
    });

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") restoreSession();
    };
    const onFocus = () => restoreSession();

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);

    return () => {
      data.subscription.unsubscribe();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    const client = supabase;
    if (!client || !authUserId) return;
    const verifiedClient = client;
    let cancelled = false;

    async function verifyAllowedUser() {
      const { data, error } = await withTimeout(verifiedClient.rpc("is_current_user_allowed"), 5000);

      if (cancelled) return;
      if (error) {
        console.error(error);
        setAuthAllowed(true);
        return;
      }
      if (data !== true) {
        await verifiedClient.auth.signOut();
        setAuthAllowed(false);
        setAuthUserId(null);
        setNotice("Votre compte n'est pas autorise a acceder a EVA Planning.");
        return;
      }

      await withTimeout(verifiedClient.rpc("ensure_current_user_profile"), 5000).catch(console.error);
      if (!cancelled) setAuthAllowed(true);
    }

    verifyAllowedUser();

    return () => {
      cancelled = true;
    };
  }, [authUserId]);

  useEffect(() => {
    if (!supabase || !authUserId || !authAllowed) return;
    const client = supabase;

    async function loadData() {
      const { data: authData } = await client.auth.getUser();
      const user = authData.user;
      if (!user) return;

      const membershipsRes = await client.from("team_members").select("*").eq("user_id", user.id);
      const memberships = (membershipsRes.data ?? []) as TeamMember[];

      const teamIds = memberships.map((membership) => membership.team_id);
      if (teamIds.length === 0) {
        setTeams([]);
        setActiveTeamId("");
        setProfiles([]);
        setSessions([]);
        setRegistrations([]);
        setAvailability([]);
        setMessages([]);
        setPolls([]);
        setPollOptions([]);
        setPollVotes([]);
        setNotice("Votre compte n'appartient encore a aucune equipe.");
        return;
      }

      const teamsRes = await client.from("teams").select("*").in("id", teamIds).order("name");
      const availableTeams = (teamsRes.data ?? []) as Team[];
      setTeams(availableTeams);

      const storedTeamId = localStorage.getItem(activeTeamStorageKey);
      const selectedTeamId = teamIds.includes(activeTeamId)
        ? activeTeamId
        : storedTeamId && teamIds.includes(storedTeamId)
          ? storedTeamId
          : teamIds[0];
      setActiveTeamId(selectedTeamId);

      const teamMemberIdsRes = await client.from("team_members").select("*").eq("team_id", selectedTeamId);
      const selectedTeamMembers = (teamMemberIdsRes.data ?? []) as TeamMember[];
      const selectedTeamMemberIds = selectedTeamMembers.map((member) => member.user_id);

      const [profilesRes, sessionsRes, registrationsRes, availabilityRes, messagesRes, pollsRes, optionsRes, votesRes] =
        await Promise.all([
          selectedTeamMemberIds.length > 0 ? client.from("profiles").select("*").in("id", selectedTeamMemberIds).order("display_name") : Promise.resolve({ data: [] }),
          client.from("eva_sessions").select("*").eq("team_id", selectedTeamId).order("starts_at"),
          client.from("session_registrations").select("*").eq("team_id", selectedTeamId),
          client.from("player_availability").select("*").eq("team_id", selectedTeamId),
          client.from("chat_messages").select("*").eq("team_id", selectedTeamId).order("created_at", { ascending: true }).limit(80),
          client.from("polls").select("*").eq("team_id", selectedTeamId).order("closes_at"),
          client.from("poll_options").select("*").eq("team_id", selectedTeamId),
          client.from("poll_votes").select("*").eq("team_id", selectedTeamId)
        ]);

      if (profilesRes.data) {
        const roleByUserId = new Map(selectedTeamMembers.map((member) => [member.user_id, member.role]));
        setProfiles((profilesRes.data as Profile[]).map((profile) => ({ ...profile, role: roleByUserId.get(profile.id) ?? "player" })));
      }
      if (sessionsRes.data) setSessions(sessionsRes.data);
      if (registrationsRes.data) setRegistrations(registrationsRes.data);
      if (availabilityRes.data) setAvailability(availabilityRes.data);
      if (messagesRes.data) setMessages(messagesRes.data);
      if (pollsRes.data) setPolls(pollsRes.data);
      if (optionsRes.data) setPollOptions(optionsRes.data);
      if (votesRes.data) setPollVotes(votesRes.data);

      const profile = profilesRes.data?.find((entry) => entry.id === user?.id);
      if (profile) {
        const membership = selectedTeamMembers.find((member) => member.user_id === profile.id);
        setCurrentUser({ ...profile, role: membership?.role ?? "player" });
        setPseudoDraft(profile.display_name);
      }
      if (!profile) {
        const fallbackProfile = {
          id: user.id,
          display_name: user.user_metadata?.name || user.email?.split("@")[0] || "Joueur",
          role: selectedTeamMembers.find((member) => member.user_id === user.id)?.role ?? "player"
        } as Profile;
        setCurrentUser(fallbackProfile);
        setPseudoDraft(fallbackProfile.display_name);
      }
      setNotice("");
    }

    loadData();

    const channel = client
      .channel("eva-planning")
      .on("postgres_changes", { event: "*", schema: "public", table: "eva_sessions" }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "session_registrations" }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "player_availability" }, loadData)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "chat_messages" }, async (payload) => {
        await loadData();
        notifyNewChatMessage(payload.new as ChatMessage);
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "chat_messages" }, loadData)
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "chat_messages" }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "polls" }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "poll_options" }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "poll_votes" }, loadData)
      .subscribe();

    return () => {
      client.removeChannel(channel);
    };
  }, [activeTeamId, authAllowed, authUserId]);

  async function enableMessageNotifications() {
    if (!("Notification" in window)) {
      setNotice("Ce navigateur ne prend pas en charge les notifications.");
      return;
    }
    if (Notification.permission === "denied") {
      setNotificationPermission("denied");
      setNotice("Notifications bloquees. Autorisez-les dans les reglages du navigateur.");
      return;
    }

    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    if (permission !== "granted") {
      setNotice("Notifications non activees.");
      return;
    }

    await savePushSubscription();
    setNotice("Notifications de chat activees.");
  }

  async function savePushSubscription() {
    if (!supabase || !isSupabaseConfigured || !("serviceWorker" in navigator) || !("PushManager" in window) || !vapidPublicKey) {
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    const existingSubscription = await registration.pushManager.getSubscription();
    const subscription =
      existingSubscription ||
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      }));
    const json = subscription.toJSON();

    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;

    await supabase.from("push_subscriptions").upsert(
      {
        user_id: currentUser.id,
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
        user_agent: navigator.userAgent,
        updated_at: new Date().toISOString()
      },
      { onConflict: "endpoint" }
    );
  }

  async function notifyNewChatMessage(message: ChatMessage) {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    if (message.team_id && message.team_id !== activeTeamId) return;
    if (message.player_id === currentUserRef.current.id) return;
    if (!document.hidden && activeTabRef.current === "chat") return;

    const author = profilesRef.current.find((profile) => profile.id === message.player_id)?.display_name || "Joueur";
    const options: NotificationOptions = {
      body: message.content,
      icon: notificationIcon,
      badge: notificationIcon,
      tag: `chat-${message.id}`,
      data: { url: "/?tab=chat" }
    };

    if ("serviceWorker" in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification(`Nouveau message de ${author}`, options);
        return;
      } catch (error) {
        console.error(error);
      }
    }

    new Notification(`Nouveau message de ${author}`, options);
  }

  async function installPwa() {
    if (!installPrompt) {
      if (isIosDevice()) {
        setShowIosInstallGuide(true);
        return;
      }
      setNotice("Utilisez le menu du navigateur pour ajouter EVA Planning a l'ecran d'accueil.");
      return;
    }

    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);

    if (choice.outcome === "accepted") {
      localStorage.setItem(installCompletedKey, "true");
      setShowInstallPrompt(false);
      return;
    }

    localStorage.setItem(installDismissedKey, "true");
    setShowInstallPrompt(false);
  }

  function dismissInstallPrompt() {
    localStorage.setItem(installDismissedKey, "true");
    setShowInstallPrompt(false);
  }

  const sortedSessions = useMemo(
    () => [...sessions].sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()),
    [sessions]
  );

  async function signInWithGoogle() {
    if (!supabase) return;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: appRedirectOrigin || window.location.origin }
    });
    if (error) setNotice(error.message);
  }

  async function signOut() {
    if (!supabase) return;
    setAuthLoading(true);
    await supabase.auth.signOut();
    setAuthUserId(null);
    setAuthAllowed(false);
    setAuthLoading(false);
  }

  async function savePseudo(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const displayName = pseudoDraft.trim();
    if (!displayName) return;

    if (supabase && isSupabaseConfigured) {
      const { data, error } = await supabase.from("profiles").update({ display_name: displayName }).eq("id", currentUser.id).select().single();
      if (error) {
        setNotice(`Erreur pseudo : ${error.message}`);
        return;
      }
      if (data) {
        setCurrentUser(data);
        setProfiles((items) => items.map((profile) => (profile.id === data.id ? data : profile)));
      }
    } else {
      setCurrentUser((profile) => ({ ...profile, display_name: displayName }));
    }
    setNotice("Pseudo mis a jour.");
  }

  async function toggleSessionAvailability(session: EvaSession) {
    if (!activeTeamId) return;
    const slot = getSessionAvailabilitySlot(session);
    const existing = availability.find((entry) => entry.player_id === currentUser.id && isAvailabilityMatching(session, entry, sessions));

    if (existing) {
      if (supabase && isSupabaseConfigured) {
        await supabase.from("player_availability").delete().eq("id", existing.id);
      }
      setAvailability((items) => items.filter((item) => item.id !== existing.id));
      return;
    }

    const next: Availability = {
      id: uid("av"),
      team_id: activeTeamId,
      player_id: currentUser.id,
      session_id: session.id,
      weekday: slot.weekday,
      start_time: slot.start_time,
      end_time: slot.end_time
    };
    if (supabase && isSupabaseConfigured) {
      const { data } = await supabase.from("player_availability").insert(next).select().single();
      if (data) setAvailability((items) => [...items, data]);
      return;
    }
    setAvailability((items) => [...items, next]);
  }

  async function deleteAvailability(availabilityId: string) {
    if (supabase && isSupabaseConfigured) await supabase.from("player_availability").delete().eq("id", availabilityId);
    setAvailability((items) => items.filter((entry) => entry.id !== availabilityId));
  }

  async function sendMessage(event: FormEvent<HTMLFormElement>, mentionTargets: MentionTargets = { mentionedPlayerIds: [], mentionAllTeam: false }) {
    event.preventDefault();
    const messageForm = event.currentTarget;
    const form = new FormData(messageForm);
    const content = String(form.get("content")).trim();
    if (!content || !activeTeamId) return;
    messageForm.reset();
    const next: ChatMessage = {
      id: uid("msg"),
      team_id: activeTeamId,
      player_id: currentUser.id,
      session_id: null,
      content,
      created_at: new Date().toISOString()
    };
    if (supabase && isSupabaseConfigured) {
      await supabase.from("chat_messages").insert(next);
      if (mentionTargets.mentionedPlayerIds.length > 0 || mentionTargets.mentionAllTeam) {
        supabase.functions.invoke("notify-mentions", {
          body: {
            messageId: next.id,
            teamId: activeTeamId,
            mentionedPlayerIds: mentionTargets.mentionedPlayerIds,
            mentionAllTeam: mentionTargets.mentionAllTeam
          }
        }).catch(console.error);
      }
    }
    setMessages((items) => [...items, next]);
  }

  async function vote(option: PollOption) {
    if (!activeTeamId) return;
    const existing = pollVotes.find((entry) => entry.poll_id === option.poll_id && entry.player_id === currentUser.id);
    const next: PollVote = { id: existing?.id || uid("vote"), team_id: activeTeamId, poll_id: option.poll_id, option_id: option.id, player_id: currentUser.id };
    if (supabase && isSupabaseConfigured) {
      await supabase.from("poll_votes").upsert(next, { onConflict: "poll_id,player_id" });
    }
    setPollVotes((items) => (existing ? items.map((item) => (item.id === existing.id ? next : item)) : [...items, next]));
  }

  async function createSession(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!activeTeamId) return;
    const form = new FormData(event.currentTarget);
    const next: EvaSession = {
      id: uid("session"),
      team_id: activeTeamId,
      title: String(form.get("title")),
      center: String(form.get("center")),
      starts_at: String(form.get("starts_at")),
      duration_minutes: Number(form.get("duration_minutes")),
      capacity: Number(form.get("capacity")),
      expert: form.get("expert") === "on",
      notes: String(form.get("notes") || ""),
      created_by: currentUser.id
    };
    if (supabase && isSupabaseConfigured) await supabase.from("eva_sessions").insert(next);
    setSessions((items) => [...items, next]);
    event.currentTarget.reset();
  }

  async function deleteSession(sessionId: string) {
    if (supabase && isSupabaseConfigured) await supabase.from("eva_sessions").delete().eq("id", sessionId);
    setSessions((items) => items.filter((session) => session.id !== sessionId));
    setRegistrations((items) => items.filter((entry) => entry.session_id !== sessionId));
  }

  async function syncEvaSessions() {
    if (!supabase || currentUser.role !== "admin" || !activeTeamId) return;

    setSyncingEva(true);
    const { data, error } = await supabase.functions.invoke("eva-sync", {
      body: { startDate: toDateKey(new Date().toISOString()), teamId: activeTeamId }
    });
    setSyncingEva(false);

    if (error) {
      setNotice(`Erreur synchro EVA : ${error.message}`);
      return;
    }

    setNotice(`Synchro EVA terminee : ${data?.sessionsCount ?? 0} sessions importees.`);
  }

  function navigateBySwipe(direction: 1 | -1) {
    const currentIndex = visibleTabs.findIndex((tab) => tab.id === activeTab);
    if (currentIndex === -1) return;

    const nextTab = visibleTabs[currentIndex + direction];
    if (nextTab) setActiveTab(nextTab.id);
  }

  function handleSwipeStart(event: TouchEvent<HTMLDivElement>) {
    const target = event.target;
    if (target instanceof Element && target.closest("input, textarea, button, a, select, .profile-popover, .mention-menu")) {
      swipeRef.current = null;
      return;
    }

    const touch = event.touches[0];
    swipeRef.current = touch ? { x: touch.clientX, y: touch.clientY, time: Date.now() } : null;
  }

  function handleSwipeEnd(event: TouchEvent<HTMLDivElement>) {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    const elapsed = Date.now() - start.time;
    const mostlyHorizontal = Math.abs(deltaX) > 70 && Math.abs(deltaX) > Math.abs(deltaY) * 1.4;

    if (!mostlyHorizontal || elapsed > 700) return;
    navigateBySwipe(deltaX < 0 ? 1 : -1);
  }

  if (authLoading || authRestoring || (isSupabaseConfigured && authUserId && !authAllowed)) {
    return <AuthScreen onGoogleSignIn={signInWithGoogle} loading />;
  }

  if (isSupabaseConfigured && !authUserId) {
    return <AuthScreen notice={notice} onGoogleSignIn={signInWithGoogle} />;
  }

  return (
    <>
      <div className="app" onTouchStart={handleSwipeStart} onTouchEnd={handleSwipeEnd}>
        <aside className="sidebar">
        <div className="brand">
          <img className="brand-mark" src="/app-icon-192.png" alt="" />
          <div>
            <strong>EVA Planning</strong>
          </div>
          <NotificationButton permission={notificationPermission} onEnable={enableMessageNotifications} compact />
          <div className="profile-menu">
            <button className="profile-button" type="button" title="Profil" onClick={() => setProfileMenuOpen((open) => !open)}>
              <UserRound size={18} />
            </button>
            {profileMenuOpen && (
              <form className="profile-popover" onSubmit={savePseudo}>
                <div>
                  <strong>{currentUser.display_name}</strong>
                  <small>{currentUser.role === "admin" ? "Administrateur" : "Joueur"}</small>
                  {activeTeam && <small>{activeTeam.name}</small>}
                </div>
                {teams.length > 1 && (
                  <select value={activeTeamId} onChange={(event) => setActiveTeamId(event.target.value)} aria-label="Equipe active">
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                )}
                <input type="text" placeholder="Pseudo joueur" value={pseudoDraft} onChange={(event) => setPseudoDraft(event.target.value)} />
                <button className="primary" type="submit">
                  <Check size={16} />
                  Enregistrer
                </button>
                {supabase && (
                  <button className="danger" type="button" onClick={signOut}>
                    Deconnexion
                  </button>
                )}
              </form>
            )}
          </div>
        </div>
        <nav>
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button className={activeTab === tab.id ? "active" : ""} key={tab.id} onClick={() => setActiveTab(tab.id)}>
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </aside>

      <main className={activeTab === "chat" ? "chat-main" : undefined}>
        <header className="topbar">
          <div>
            <p className="eyebrow">Planning d'equipe</p>
            <h1>{pageTitles[activeTab]}</h1>
          </div>
          <NotificationButton permission={notificationPermission} onEnable={enableMessageNotifications} />
        </header>

        {activeTab === "sessions" && (
          <SessionsView
            sessions={sortedSessions}
            registrations={registrations}
            profiles={profiles}
            availability={availability}
            currentUser={currentUser}
            onToggleAvailability={toggleSessionAvailability}
          />
        )}
        {activeTab === "availability" && (
          <AvailabilityView
            profiles={profiles}
            availability={availability}
            currentUser={currentUser}
            sessions={sortedSessions}
            onDelete={deleteAvailability}
          />
        )}
        {activeTab === "chat" && (
          <ChatView messages={messages} profiles={profiles} sessions={sessions} currentUser={currentUser} onSend={sendMessage} />
        )}
        {activeTab === "polls" && (
          <PollsView polls={polls} options={pollOptions} votes={pollVotes} currentUser={currentUser} onVote={vote} />
        )}
        {activeTab === "admin" && currentUser.role === "admin" && (
          <AdminView
            profiles={profiles}
            sessions={sortedSessions}
            registrations={registrations}
            syncingEva={syncingEva}
            onCreateSession={createSession}
            onDeleteSession={deleteSession}
            onSyncEva={syncEvaSessions}
          />
        )}
        </main>
      </div>
      {welcomeTeam && <TeamWelcomeDialog teamName={welcomeTeam.name} onClose={() => setWelcomeTeam(null)} />}
      {showInstallPrompt && <InstallAppPrompt installPromptReady={Boolean(installPrompt)} onInstall={installPwa} onDismiss={dismissInstallPrompt} />}
      {showIosInstallGuide && <IosInstallGuide onClose={() => setShowIosInstallGuide(false)} />}
    </>
  );
}

function TeamWelcomeDialog({ teamName, onClose }: { teamName: string; onClose: () => void }) {
  return (
    <div className="welcome-backdrop" role="dialog" aria-modal="true" aria-labelledby="team-welcome-title">
      <section className="welcome-dialog">
        <button className="install-close" type="button" onClick={onClose} title="Fermer" aria-label="Fermer">
          <X size={18} />
        </button>
        <img className="brand-mark" src="/app-icon-192.png" alt="" />
        <p className="eyebrow">Equipe active</p>
        <h2 id="team-welcome-title">Bonjour {teamName}</h2>
        <p>Votre espace EVA Planning est pret.</p>
        <button className="primary" type="button" onClick={onClose}>
          Continuer
        </button>
      </section>
    </div>
  );
}

function AuthScreen({
  notice,
  loading,
  onGoogleSignIn
}: {
  notice?: string;
  loading?: boolean;
  onGoogleSignIn: () => void;
}) {
  return (
    <main className="auth-screen">
      <section className="auth-card">
        <div className="brand auth-brand">
          <img className="brand-mark" src="/app-icon-192.png" alt="" />
          <div>
            <strong>EVA Planning</strong>
          </div>
        </div>
        <h1>Bienvenue la TEAM</h1>
        <p>Connectez-vous avec Google, puis choisissez votre pseudo joueur.</p>
        <button className="primary google-auth-button" type="button" onClick={onGoogleSignIn} disabled={loading}>
          <LogIn size={16} />
          {loading ? "Verification..." : "Connexion avec Google"}
        </button>
        {notice && (
          <div className="notice auth-notice">
            {notice}
          </div>
        )}
      </section>
    </main>
  );
}

function urlBase64ToUint8Array(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
}

function withTimeout<T>(promise: PromiseLike<T>, timeoutMs: number) {
  return new Promise<T>((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error("Operation timed out")), timeoutMs);
    Promise.resolve(promise)
      .then(resolve)
      .catch(reject)
      .finally(() => window.clearTimeout(timeout));
  });
}

function IosInstallGuide({ onClose }: { onClose: () => void }) {
  return (
    <div className="install-guide-backdrop" role="dialog" aria-modal="true" aria-labelledby="ios-install-title">
      <section className="install-guide">
        <button className="install-close" type="button" onClick={onClose} title="Fermer" aria-label="Fermer">
          <X size={18} />
        </button>
        <div className="install-guide-icon">
          <Share2 size={22} />
        </div>
        <h2 id="ios-install-title">Installer EVA Planning</h2>
        <ol>
          <li>Appuyez sur le bouton Partager de Safari.</li>
          <li>Choisissez Ajouter a l'ecran d'accueil.</li>
          <li>Validez avec Ajouter.</li>
        </ol>
        <button className="primary" type="button" onClick={onClose}>
          Compris
        </button>
      </section>
    </div>
  );
}

function InstallAppPrompt({
  installPromptReady,
  onInstall,
  onDismiss
}: {
  installPromptReady: boolean;
  onInstall: () => void;
  onDismiss: () => void;
}) {
  return (
    <aside className="install-prompt" aria-label="Installation de l'application">
      <button className="install-close" type="button" onClick={onDismiss} title="Fermer" aria-label="Fermer">
        <X size={18} />
      </button>
      <div className="install-icon">
        <Download size={20} />
      </div>
      <div className="install-copy">
        <strong>Installer EVA Planning</strong>
        <span>{installPromptReady ? "Ajoutez l'application a votre ecran d'accueil." : "Sur iPhone : Partager puis Ajouter a l'ecran d'accueil."}</span>
      </div>
      <button className="primary install-action" type="button" onClick={onInstall}>
        Installer
      </button>
    </aside>
  );
}

function NotificationButton({
  permission,
  compact,
  onEnable
}: {
  permission: NotificationPermission;
  compact?: boolean;
  onEnable: () => void;
}) {
  if (!("Notification" in window)) return null;

  const active = permission === "granted";
  const blocked = permission === "denied";
  const Icon = active ? Bell : BellOff;
  const label = active ? "Notifications activees" : blocked ? "Notifications bloquees" : "Activer notifications";

  return (
    <button
      className={`notification-button ${active ? "enabled" : ""} ${compact ? "compact" : ""}`}
      type="button"
      title={label}
      onClick={onEnable}
      aria-label={label}
    >
      <Icon size={17} />
      {!compact && <span>{label}</span>}
    </button>
  );
}

function SessionsView({
  sessions,
  registrations,
  profiles,
  availability,
  currentUser,
  onToggleAvailability
}: {
  sessions: EvaSession[];
  registrations: Registration[];
  profiles: Profile[];
  availability: Availability[];
  currentUser: Profile;
  onToggleAvailability: (session: EvaSession) => void;
}) {
  const [now, setNow] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => toDateKey(new Date().toISOString()));
  const upcomingSessions = useMemo(() => sessions.filter((session) => new Date(session.starts_at) > now), [sessions, now]);
  const monthDays = useMemo(() => getRollingMonthDays(now), [now]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const sessionsByDay = useMemo(() => {
    return upcomingSessions.reduce<Record<string, EvaSession[]>>((acc, session) => {
      const key = toDateKey(session.starts_at);
      acc[key] = [...(acc[key] || []), session];
      return acc;
    }, {});
  }, [upcomingSessions]);

  useEffect(() => {
    if (monthDays.includes(selectedDate)) return;
    setSelectedDate(toDateKey(now.toISOString()));
  }, [monthDays, now, selectedDate]);

  const selectedSessions = sessionsByDay[selectedDate] || [];

  return (
    <section className="daily-planning">
      <div className="day-strip" aria-label="Jours du mois">
        {monthDays.map((day) => {
          const count = sessionsByDay[day]?.length || 0;
          return (
            <button className={day === selectedDate ? "active" : ""} key={day} onClick={() => setSelectedDate(day)}>
              <span>{formatDayName(day)}</span>
              <strong>{formatDayNumber(day)}</strong>
              <small>{count} session{count > 1 ? "s" : ""}</small>
            </button>
          );
        })}
      </div>

      <div className="day-heading">
        <div>
          <p className="eyebrow">Planning du jour</p>
          <h2>{formatFullDay(selectedDate)}</h2>
        </div>
        <span>{selectedSessions.length} session{selectedSessions.length > 1 ? "s" : ""}</span>
      </div>

      {selectedSessions.length === 0 ? (
        <div className="empty-state">
          <CalendarClock size={28} />
          <h2>Aucune session sur ce jour</h2>
          <p>Les creneaux apparaitront ici apres creation admin ou apres la synchronisation EVA du mois.</p>
        </div>
      ) : (
        <div className="session-grid">
          {selectedSessions.map((session) => {
        const count = visibleTakenSeats(session, registrations);
        const remaining = session.capacity - count;
        const status = sessionStatus(session, registrations);
        const sessionUrl = getEvaSessionUrl(session);
        const currentUserAvailable = availability.some((entry) => entry.player_id === currentUser.id && isAvailabilityMatching(session, entry, sessions));
        const availablePlayers = profiles.filter((profile) =>
          availability.some((entry) => entry.player_id === profile.id && isAvailabilityMatching(session, entry, sessions))
        );
        return (
          <article className="session-card" key={session.id}>
            <div className="card-head">
              <span className={`status ${status}`}>{status === "full" ? "Complet" : status === "almost-full" ? "Presque complet" : "Disponible"}</span>
              {session.expert && <span className="tag">Expert</span>}
            </div>
            <h2>{formatSessionTime(session.starts_at)} - Le Mans</h2>
            <div className="capacity">
              <div style={{ width: `${Math.min(100, (count / session.capacity) * 100)}%` }} />
            </div>
            <div className="split">
              <strong>{count} inscrits</strong>
              <span>{Math.max(0, remaining)} places restantes</span>
            </div>
            {availablePlayers.length > 0 && (
              <div className="players">
                {availablePlayers.map((player) => <span key={player.id}>{player.display_name}</span>)}
              </div>
            )}
            <div className="session-actions" aria-label="Actions session">
              <button className={currentUserAvailable ? "danger" : "primary"} type="button" onClick={() => onToggleAvailability(session)}>
                {currentUserAvailable ? <CalendarMinus size={16} /> : <CalendarPlus size={16} />}
                {currentUserAvailable ? "Retirer dispo" : "Ajouter dispo"}
              </button>
              <a className={`primary session-link ${status === "full" ? "disabled" : ""}`} href={sessionUrl} target="_blank" rel="noreferrer">
                <ExternalLink size={16} />
                {status === "full" ? "Complet" : "S'inscrire"}
              </a>
            </div>
          </article>
        );
          })}
        </div>
      )}
    </section>
  );
}

function toDateKey(value: string) {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getRollingMonthDays(now: Date) {
  const cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  const end = new Date(cursor);
  end.setMonth(end.getMonth() + 1);
  const days: string[] = [];

  while (cursor < end) {
    const dayKey = toDateKey(cursor.toISOString());
    days.push(dayKey);
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

function formatDayName(value: string) {
  return new Intl.DateTimeFormat("fr-FR", { weekday: "short" }).format(new Date(`${value}T12:00:00`));
}

function formatDayNumber(value: string) {
  return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short" }).format(new Date(`${value}T12:00:00`));
}

function formatFullDay(value: string) {
  return new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).format(
    new Date(`${value}T12:00:00`)
  );
}

function formatSessionTime(value: string) {
  return new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function formatChatTimestamp(value: string, now: Date) {
  const date = new Date(value);
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.max(0, Math.floor(diffMs / 1000));
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = daysBetween(date, now);

  if (diffSeconds < 45) return "A l'instant";
  if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
  if (diffHours < 24 && diffDays === 0) return `Il y a ${diffHours} h`;
  if (diffDays === 1) return `Hier a ${formatMessageTime(date)}`;
  if (diffDays < 7) return `Il y a ${diffDays} jours`;

  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" })
  }).format(date);
}

function formatFullMessageDate(value: string) {
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "full",
    timeStyle: "short"
  }).format(new Date(value));
}

function formatMessageTime(date: Date) {
  return new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(date);
}

function daysBetween(date: Date, now: Date) {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.max(0, Math.floor((end - start) / 86_400_000));
}

function formatShortDate(value: string) {
  return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }).format(new Date(value));
}

function formatShortWeekday(value: string) {
  return capitalize(new Intl.DateTimeFormat("fr-FR", { weekday: "short" }).format(new Date(value)).replace(".", ""));
}

function formatShortDayDate(value: string) {
  return `${formatShortWeekday(value)} ${formatShortDate(value)}`;
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getSessionAvailabilitySlot(session: EvaSession) {
  const start = new Date(session.starts_at);
  const end = new Date(start.getTime() + session.duration_minutes * 60_000);
  return {
    weekday: start.getDay(),
    start_time: formatTimeInput(start),
    end_time: formatTimeInput(end)
  };
}

function formatTimeInput(date: Date) {
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function formatAvailabilityTime(value: string) {
  return value.slice(0, 5);
}

function findAvailabilitySession(availability: Availability, sessions: EvaSession[]) {
  if (availability.session_id) {
    return sessions.find((session) => session.id === availability.session_id);
  }

  return sessions.find((session) => {
    const slot = getSessionAvailabilitySlot(session);
    return (
      new Date(session.starts_at) > new Date() &&
      availability.weekday === slot.weekday &&
      formatAvailabilityTime(availability.start_time) === slot.start_time &&
      formatAvailabilityTime(availability.end_time) === slot.end_time
    );
  });
}

function formatAvailabilityLabel(availability: Availability, sessions: EvaSession[]) {
  const session = findAvailabilitySession(availability, sessions);
  const date = session ? formatShortDayDate(session.starts_at) : weekdays[availability.weekday];
  return `${date} ${formatAvailabilityTime(availability.start_time)}`;
}

function getEvaSessionUrl(session: EvaSession) {
  if (session.eva_location_id) {
    const date = toDateKey(session.starts_at);
    const params = new URLSearchParams({
      locationId: String(session.eva_location_id),
      seatCount: "1",
      currentDate: date
    });
    return `https://app.eva.gg/fr-FR/booking/calendar?${params.toString()}`;
  }
  return "https://app.eva.gg/fr-FR/booking";
}

function AvailabilityView({
  profiles,
  availability,
  currentUser,
  sessions,
  onDelete
}: {
  profiles: Profile[];
  availability: Availability[];
  currentUser: Profile;
  sessions: EvaSession[];
  onDelete: (availabilityId: string) => void;
}) {
  const myAvailability = availability.filter((entry) => entry.player_id === currentUser.id);
  const bestSlots = useMemo(() => {
    return sessions
      .filter((session) => new Date(session.starts_at) > new Date())
      .map((session) => {
        const availablePlayers = profiles.filter((profile) =>
          availability.some((entry) => entry.player_id === profile.id && isAvailabilityMatching(session, entry, sessions))
        );
        return { session, availablePlayers };
      })
      .filter((slot) => slot.availablePlayers.length >= 2)
      .sort((a, b) => {
        const playersDiff = b.availablePlayers.length - a.availablePlayers.length;
        if (playersDiff !== 0) return playersDiff;
        return new Date(a.session.starts_at).getTime() - new Date(b.session.starts_at).getTime();
      });
  }, [availability, profiles, sessions]);

  return (
    <section className="availability-layout">
      <div className="panel priority-panel">
        <div className="panel-title-row">
          <div>
            <h2>Meilleurs creneaux</h2>
            <p>Sessions ou au moins 2 joueurs sont disponibles.</p>
          </div>
          <span>{bestSlots.length}</span>
        </div>

        {bestSlots.length > 0 ? (
          <div className="best-slot-list">
            {bestSlots.map(({ session, availablePlayers }) => (
              <article className="best-slot-card" key={session.id}>
                <div className="best-slot-main">
                  <div>
                    <span>{formatShortDayDate(session.starts_at)}</span>
                    <h3>{formatSessionTime(session.starts_at)} - {session.center}</h3>
                  </div>
                  <strong>{availablePlayers.length} joueurs</strong>
                </div>
                <div className="best-slot-meta">
                  <span>{session.title}</span>
                  <span>{visibleTakenSeats(session, [])}/{session.capacity} inscrits EVA</span>
                </div>
                <div className="availability-chips">
                  {availablePlayers.map((player) => (
                    <span key={player.id}>{player.display_name}</span>
                  ))}
                </div>
                <a className="primary session-link best-slot-link" href={getEvaSessionUrl(session)} target="_blank" rel="noreferrer">
                  <ExternalLink size={16} />
                  Reserver cette session
                </a>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-state compact-empty">
            <CalendarClock size={26} />
            <h2>Aucun creneau commun</h2>
            <p>Ajoutez vos dispos ou synchronisez les sessions EVA pour faire ressortir les meilleurs horaires.</p>
          </div>
        )}
      </div>

      <div className="panel">
        <h2>Mes dispos</h2>
        <div className="my-availability-list">
          {myAvailability.length > 0 ? (
            myAvailability.map((entry) => {
              const session = findAvailabilitySession(entry, sessions);
              return (
                <div className="my-availability-row" key={entry.id}>
                  <div>
                    <strong>{session ? formatShortDayDate(session.starts_at) : weekdays[entry.weekday]}</strong>
                    <span>{formatAvailabilityTime(entry.start_time)}</span>
                  </div>
                  <button className="icon-danger" type="button" onClick={() => onDelete(entry.id)} title="Retirer cette dispo">
                    <X size={16} />
                  </button>
                </div>
              );
            })
          ) : (
            <div className="empty-inline">Aucune dispo renseignee.</div>
          )}
        </div>
        <h3>Vue equipe</h3>
        <div className="availability-list compact-list">
          {profiles.map((profile) => {
            const entries = availability.filter((entry) => entry.player_id === profile.id);
            return (
              <div className="availability-row" key={profile.id}>
                <div className="availability-player">
                  <strong>{profile.display_name}</strong>
                  <span>{entries.length} dispo{entries.length > 1 ? "s" : ""}</span>
                </div>
                {entries.length > 0 ? (
                  <div className="availability-chips">
                    {entries.map((entry) => (
                      <span key={entry.id}>{formatAvailabilityLabel(entry, sessions)}</span>
                    ))}
                  </div>
                ) : (
                  <span className="muted">Non renseigne</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ChatView({
  messages,
  profiles,
  sessions,
  currentUser,
  onSend
}: {
  messages: ChatMessage[];
  profiles: Profile[];
  sessions: EvaSession[];
  currentUser: Profile;
  onSend: (event: FormEvent<HTMLFormElement>, mentionTargets: MentionTargets) => void;
}) {
  const [messageDraft, setMessageDraft] = useState("");
  const [now, setNow] = useState(() => new Date());
  const feedRef = useRef<HTMLDivElement | null>(null);
  const mentionQuery = getActiveMentionQuery(messageDraft);
  const mentionSuggestions =
    mentionQuery === null
      ? []
      : [
          ...(teamMentionLabel.toLowerCase().includes(mentionQuery.toLowerCase()) ? [{ id: teamMentionLabel, label: teamMentionLabel, team: true }] : []),
          ...profiles
            .filter((profile) => profile.id !== currentUser.id)
            .filter((profile) => profile.display_name.toLowerCase().includes(mentionQuery.toLowerCase()))
            .map((profile) => ({ id: profile.id, label: profile.display_name, team: false }))
        ].slice(0, 5);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const feed = feedRef.current;
    if (!feed) return;

    requestAnimationFrame(() => {
      feed.scrollTop = feed.scrollHeight;
    });
  }, [messages.length]);

  function selectMention(label: string) {
    setMessageDraft((draft) => draft.replace(/(^|\s)@([^\s@]*)$/, `$1@${label} `));
  }

  function submitMessage(event: FormEvent<HTMLFormElement>) {
    if (!messageDraft.trim()) {
      event.preventDefault();
      return;
    }

    onSend(event, getMentionTargets(messageDraft, profiles, currentUser.id));
    setMessageDraft("");
  }

  return (
    <section className="chat-panel">
      <div className="chat-titlebar">
        <div>
          <strong>Chat d'equipe</strong>
          <small>{profiles.length} joueurs</small>
        </div>
      </div>
      <div className="chat-feed" ref={feedRef}>
        {messages.map((message) => {
          const author = profiles.find((profile) => profile.id === message.player_id);
          const session = sessions.find((entry) => entry.id === message.session_id);
          const mine = message.player_id === currentUser.id;
          return (
            <div className={`message ${mine ? "mine" : ""}`} key={message.id}>
              <small>
                <span>{author?.display_name || "Joueur"}</span>
                {session ? ` · ${session.title}` : ""}
                <time dateTime={message.created_at} title={formatFullMessageDate(message.created_at)}>
                  {formatChatTimestamp(message.created_at, now)}
                </time>
              </small>
              <p>{message.content}</p>
            </div>
          );
        })}
      </div>
      <form className="chat-form" onSubmit={submitMessage}>
        {mentionSuggestions.length > 0 && (
          <div className="mention-menu">
            {mentionSuggestions.map((mention) => (
              <button key={mention.id} type="button" onClick={() => selectMention(mention.label)}>
                @{mention.label}
                {mention.team ? " · toute l'equipe" : ""}
              </button>
            ))}
          </div>
        )}
        <input
          name="content"
          placeholder="Envoyer un message"
          value={messageDraft}
          onChange={(event) => setMessageDraft(event.target.value)}
          autoComplete="off"
        />
        <button className="primary" type="submit">
          <ChevronRight size={16} />
        </button>
      </form>
    </section>
  );
}

function getActiveMentionQuery(value: string) {
  const match = value.match(/(?:^|\s)@([^\s@]*)$/);
  return match ? match[1] : null;
}

function getMentionTargets(content: string, profiles: Profile[], currentUserId: string): MentionTargets {
  const normalizedContent = content.toLowerCase();
  const mentionAllTeam = new RegExp(`(^|\\s)@${teamMentionLabel}(?=\\s|$|[.,!?;:])`, "i").test(content);
  const mentionedPlayerIds = profiles
    .filter((profile) => profile.id !== currentUserId)
    .filter((profile) => normalizedContent.includes(`@${profile.display_name.toLowerCase()}`))
    .map((profile) => profile.id);

  return {
    mentionAllTeam,
    mentionedPlayerIds
  };
}

function PollsView({
  polls,
  options,
  votes,
  currentUser,
  onVote
}: {
  polls: Poll[];
  options: PollOption[];
  votes: PollVote[];
  currentUser: Profile;
  onVote: (option: PollOption) => void;
}) {
  return (
    <section className="poll-grid">
      {polls.map((poll) => {
        const pollOptions = options.filter((option) => option.poll_id === poll.id);
        const totalVotes = votes.filter((voteEntry) => voteEntry.poll_id === poll.id).length || 1;
        const myVote = votes.find((voteEntry) => voteEntry.poll_id === poll.id && voteEntry.player_id === currentUser.id);
        return (
          <article className="panel poll" key={poll.id}>
            <div className="card-head">
              <span className="tag">Fin {formatSessionDate(poll.closes_at)}</span>
            </div>
            <h2>{poll.title}</h2>
            {pollOptions.map((option) => {
              const count = votes.filter((voteEntry) => voteEntry.option_id === option.id).length;
              return (
                <button className={`poll-option ${myVote?.option_id === option.id ? "selected" : ""}`} key={option.id} onClick={() => onVote(option)}>
                  <span>{option.label}</span>
                  <strong>{count}</strong>
                  <div style={{ width: `${(count / totalVotes) * 100}%` }} />
                </button>
              );
            })}
          </article>
        );
      })}
    </section>
  );
}

function AdminView({
  profiles,
  sessions,
  registrations,
  syncingEva,
  onCreateSession,
  onDeleteSession,
  onSyncEva
}: {
  profiles: Profile[];
  sessions: EvaSession[];
  registrations: Registration[];
  syncingEva: boolean;
  onCreateSession: (event: FormEvent<HTMLFormElement>) => void;
  onDeleteSession: (sessionId: string) => void;
  onSyncEva: () => void;
}) {
  return (
    <section className="two-column admin">
      <div className="panel">
        <h2>Nouvelle session</h2>
        <form className="form-stack" onSubmit={onCreateSession}>
          <input name="title" placeholder="Nom de la session" required />
          <input name="center" placeholder="Centre EVA" required />
          <input name="starts_at" type="datetime-local" required />
          <div className="inline-fields">
            <input name="duration_minutes" type="number" min="20" defaultValue="40" />
            <input name="capacity" type="number" min="1" defaultValue="6" />
          </div>
          <label className="check-line">
            <input name="expert" type="checkbox" />
            Session expert
          </label>
          <textarea name="notes" placeholder="Notes internes" />
          <button className="primary" type="submit">
            <Plus size={16} />
            Creer
          </button>
        </form>
      </div>
      <div className="panel">
        <h2>Gestion equipe</h2>
        <button className="primary sync-action" type="button" onClick={onSyncEva} disabled={syncingEva}>
          <RefreshCw size={16} />
          {syncingEva ? "Synchronisation..." : "Synchroniser EVA maintenant"}
        </button>
        <div className="admin-list">
          {sessions.map((session) => (
            <div className="admin-row" key={session.id}>
              <div>
                <strong>{session.title}</strong>
                <span>
                  {registeredCount(session.id, registrations)}/{session.capacity} · {session.center}
                </span>
              </div>
              <button className="icon-danger" onClick={() => onDeleteSession(session.id)} title="Supprimer">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <h3>Joueurs</h3>
        <div className="players roster">
          {profiles.map((profile) => (
            <span key={profile.id}>{profile.display_name}</span>
          ))}
        </div>
      </div>
    </section>
  );
}
